import React, { useRef, useState } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { consultantApplicationApi } from "@/lib/api/consultant-application";
import { ACCEPTED_CV_EXTENSIONS, MAX_CV_SIZE_MB, validateCvFile as validateFile } from "@/lib/cv";

interface CvUploadProps {
  sessionId: string;
  cvUrl: string | null;
  onUploadSuccess: (cvUrl: string, fileName: string) => void;
}

export const CvUpload: React.FC<CvUploadProps> = ({ sessionId, cvUrl, onUploadSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      // Reset so the same file can be re-selected after fixing the issue
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const result = await consultantApplicationApi.uploadCv(sessionId, file);
      setUploadedFileName(file.name);
      onUploadSuccess(result.cv_url, file.name);
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    setUploadedFileName(null);
    setError(null);
    onUploadSuccess("", "");
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const hasFile = Boolean(uploadedFileName || cvUrl);

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <FileText className="w-4 h-4" />
        CV / Resume
      </Label>

      {hasFile ? (
        <div className="flex items-center gap-3 rounded-md border border-input bg-muted/40 px-3 py-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-sm">{uploadedFileName ?? "CV uploaded"}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
            onClick={handleRemove}
            aria-label="Remove CV"
          >
            <X className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleButtonClick}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="mr-1 h-3 w-3" />
                Replace
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleButtonClick}
            disabled={isUploading}
            className="flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload CV
              </>
            )}
          </Button>
          <span className="text-xs text-muted-foreground">PDF or DOCX, max {MAX_CV_SIZE_MB}MB</span>
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      <Input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_CV_EXTENSIONS}
        onChange={handleFileChange}
        className="hidden"
        aria-label="Upload CV file"
      />
    </div>
  );
};
